import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import IncomingScanStatusBadge from './IncomingScanStatusBadge';
import IncomingShipmentExpectedBlock from './IncomingShipmentExpectedBlock';
import IncomingShipmentScanCard from './IncomingShipmentScanCard';
import { incomingShipmentApi } from '../../../../api/incomingShipmentApi';
import { shipmentStatusLabel, shipmentStatusBadgeClass, SHIPMENT_STATUS_SELECT_ORDER } from './incomingShipmentStatusUi';
import IncomingStatusBadgePicker from './IncomingStatusBadgePicker';
import { allocateIncomingScans, incomingBulkReadiness } from '../../../../utils/incomingScanAllocation';

const IncomingShipmentDetailMobileView = ({
  shipmentId,
  shipment,
  barcode,
  setBarcode,
  submitScan,
  inputRef,
  submitting,
  loading,
  error,
  bindItemId,
  bindSearch,
  setBindSearch,
  bindSearchResults,
  bindSearchLoading,
  bindSubmitting,
  openBindDropdown,
  bindProductToItem,
  itemPhotoUploadingId,
  uploadItemPhoto,
  deleteItemPhoto,
  deleteScannedItem,
  saveItemQuantity,
  itemDeletingId,
  itemQuantitySavingId,
  onAddExpectedItem,
  onDeleteExpectedItem,
  addingExpected,
  deletingExpectedId,
  onOpenExcelImport,
  onSaveExpectedBarcode,
  savingExpectedBarcodeId,
  onBindExpectedItem,
  bindingExpectedId,
  onPatchShipmentStatus,
  savingShipmentStatus,
  onChangeExpectedItemStatus,
  savingExpectedStatusId,
  onPostExpectedToSite,
  postingExpectedToSiteId,
  onPostAllReadyToSite,
  postingAllReady,
  expectedDeleteConfirmOpen,
  onGenerateExpectedFromScanned,
  generatingExpectedFromScanned,
  onSaveExpectedPurchasePrice,
  savingExpectedPurchasePriceId
}) => {
  const shipmentStatusOptionsFor = (current) => {
    const cur = current || 'draft';
    const set = new Set(SHIPMENT_STATUS_SELECT_ORDER);
    const list = [...SHIPMENT_STATUS_SELECT_ORDER];
    if (cur && !set.has(cur)) list.unshift(cur);
    return list;
  };
  const parseItemMs = (it) => {
    if (!it) return 0;
    if (it.scannedAtUtc) {
      const t = Date.parse(it.scannedAtUtc);
      if (!Number.isNaN(t)) return t;
    }
    const raw = String(it.scannedAt || '').trim();
    const m = raw.match(/^(\d{1,2})\.(\d{1,2}),\s*(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
    if (!m) return 0;
    const now = new Date();
    return new Date(now.getFullYear(), Number(m[2]) - 1, Number(m[1]), Number(m[3]), Number(m[4]), Number(m[5] || 0)).getTime();
  };

  const timeGroupLabel = (ms) => {
    if (!ms) return 'Раніше';
    const diffMin = Math.max(0, Math.floor((Date.now() - ms) / 60000));
    if (diffMin < 1) return 'Щойно';
    if (diffMin < 60) return `${diffMin} хв тому`;
    const d = new Date(ms);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Сьогодні';
    return 'Раніше';
  };

  const scannedItems = useMemo(() => shipment?.scannedItems || [], [shipment?.scannedItems]);
  const expectedItems = useMemo(() => shipment?.expectedItems || [], [shipment?.expectedItems]);
  const [scannedOpen, setScannedOpen] = useState(true);
  const [downloadBusy, setDownloadBusy] = useState(false);

  const files = shipment?.files || [];
  const allAwsNames = files.map((f) => f?.awsName).filter(Boolean);
  const uniqueAwsNames = Array.from(new Set(allAwsNames.map((x) => String(x))));

  const downloadSourceFile = (awsName) => {
    const name = (awsName || '').trim();
    if (!name || downloadBusy) return;
    setDownloadBusy(true);
    incomingShipmentApi.downloadSourceFile(shipmentId, name)
      .then((res) => {
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = String(name || `incoming-shipment-${shipmentId}.xlsx`);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {})
      .finally(() => setDownloadBusy(false));
  };

  const scanAllocation = useMemo(() => allocateIncomingScans(expectedItems, scannedItems), [expectedItems, scannedItems]);
  const scanSummary = scanAllocation.summary;
  const bulkPostReady = useMemo(() => incomingBulkReadiness(expectedItems, scanAllocation), [expectedItems, scanAllocation]);

  const groupedItems = useMemo(() => {
    const groups = [];
    let prevLabel = null;
    scannedItems.forEach((it) => {
      const label = timeGroupLabel(parseItemMs(it));
      if (!groups.length || prevLabel !== label) {
        groups.push({ label, items: [it] });
        prevLabel = label;
      } else {
        groups[groups.length - 1].items.push(it);
      }
    });
    return groups;
  }, [scannedItems]);

  return (
    <>
      <div className='sticky-top bg-white pt-2 pb-2' style={{ zIndex: 20 }}>
        <div className='d-flex justify-content-between align-items-start mb-2' style={{ gap: '0.5rem' }}>
          <div className='flex-grow-1' style={{ minWidth: 0 }}>
            <h4 className='font-weight-bold mb-1'>Поставка: {shipment?.name || `#${shipmentId}`}</h4>
            {onPatchShipmentStatus ? (
              <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.35rem' }}>
                <span className='text-muted small mb-0'>Статус</span>
                <IncomingStatusBadgePicker
                  value={shipment?.status || 'draft'}
                  options={shipmentStatusOptionsFor(shipment?.status)}
                  labelOf={shipmentStatusLabel}
                  badgeClassOf={shipmentStatusBadgeClass}
                  onPick={onPatchShipmentStatus}
                  disabled={Boolean(savingShipmentStatus)}
                  title='Статус поставки — натисніть, щоб змінити'
                />
              </div>
            ) : null}
          </div>
          <div className='d-flex align-items-center flex-shrink-0' style={{ gap: '0.5rem' }}>
            {uniqueAwsNames.length ? (
              <button
                type='button'
                className='btn btn-outline-primary btn-sm'
                onClick={() => downloadSourceFile(uniqueAwsNames[0])}
                disabled={downloadBusy}
                title={uniqueAwsNames.length > 1 ? `Скачати (файлів: ${uniqueAwsNames.length})` : 'Скачати файл'}
              >
                {downloadBusy ? '…' : <i className='bi bi-download' aria-hidden />}
              </button>
            ) : null}
            <Link to='/admin/incoming-shipments' className='btn btn-outline-dark btn-sm'>
              <i className='bi bi-arrow-left mr-1'></i>
              Назад
            </Link>
          </div>
        </div>

        <div className='alert alert-light border py-2 px-2 mb-2'>
          <div className='d-flex flex-wrap align-items-start justify-content-between' style={{ gap: '0.35rem 0.5rem' }}>
            <div className='flex-grow-1' style={{ minWidth: 0 }}>
              <div className='font-weight-bold text-dark mb-1' style={{ fontSize: '1.05rem' }}>Результат сканування</div>
              <div className='d-flex flex-wrap align-items-center' style={{ gap: '0.4rem 0.75rem', fontSize: '1.02rem' }}>
                <div>
                  <span className='badge badge-success mr-1'>Повністю</span>
                  <span className='font-weight-bold'>{scanSummary.full}</span> / {scanSummary.expectedCount}
                </div>
                <div>
                  <span className='badge badge-warning mr-1'>Частково</span>
                  <span className='font-weight-bold'>{scanSummary.partial}</span>
                </div>
                <div>
                  <span className='badge badge-danger mr-1'>Більше</span>
                  <span className='font-weight-bold'>{scanSummary.over}</span>
                </div>
                <div>
                  <span className='text-muted'>К-ть</span>{' '}
                  <span className='font-weight-bold'>{scanSummary.scannedQtyMatchedTotal}</span> / {scanSummary.expectedQtyTotal}
                </div>
              </div>
            </div>
            {onPostAllReadyToSite && bulkPostReady.show ? (
              <button
                type='button'
                className='btn btn-sm btn-success flex-shrink-0 align-self-center'
                onClick={() => void onPostAllReadyToSite()}
                disabled={Boolean(
                  !bulkPostReady.canPost || postingAllReady || postingExpectedToSiteId != null || addingExpected || deletingExpectedId != null || savingExpectedPurchasePriceId != null || bindingExpectedId != null || savingExpectedBarcodeId != null || savingExpectedStatusId != null
                )}
                title={bulkPostReady.reason || `Внести на склад усі готові позиції (${bulkPostReady.count})`}
              >
                {postingAllReady ? '…' : 'Внести все'}
              </button>
            ) : null}
          </div>
        </div>

      {scanSummary.unmatchedQuantity > 0 && <div className='alert alert-warning' role='status'>
        Невраховані скани: <b>{scanSummary.unmatchedQuantity} шт.</b> Зіставте їх з очікуваними товарами перед завершенням поставки.
      </div>}
      {bulkPostReady.show && bulkPostReady.reason && <p className='text-danger small' role='status'>{bulkPostReady.reason}</p>}

        <div className='mb-3' data-incoming-expected-block='true'>
          <IncomingShipmentExpectedBlock
            items={shipment?.expectedItems}
            scannedItems={shipment?.scannedItems}
            onAddItem={onAddExpectedItem}
            onDeleteItem={onDeleteExpectedItem}
            adding={addingExpected}
            deletingId={deletingExpectedId}
            onOpenExcelImport={onOpenExcelImport}
            onSaveExpectedBarcode={onSaveExpectedBarcode}
            savingBarcodeId={savingExpectedBarcodeId}
            onBindExpectedItem={onBindExpectedItem}
            bindingExpectedId={bindingExpectedId}
            onChangeExpectedItemStatus={onChangeExpectedItemStatus}
            savingExpectedStatusId={savingExpectedStatusId}
            onPostExpectedToSite={onPostExpectedToSite}
            postingExpectedToSiteId={postingExpectedToSiteId}
            expectedDeleteConfirmOpen={expectedDeleteConfirmOpen}
            onSaveExpectedPurchasePrice={onSaveExpectedPurchasePrice}
            savingPurchasePriceId={savingExpectedPurchasePriceId}
            incomingToolbarBusy={Boolean(postingAllReady || postingExpectedToSiteId != null || loading)}
            onGenerateExpectedFromScanned={onGenerateExpectedFromScanned}
            generatingExpectedFromScanned={generatingExpectedFromScanned}
          />
        </div>

      </div>

      <div className='card mb-3'>
        <div
          className='card-header d-flex align-items-center py-2'
          style={{ cursor: 'pointer' }}
          onClick={() => setScannedOpen(!scannedOpen)}
          role='button'
          tabIndex={0}
          aria-expanded={scannedOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setScannedOpen(!scannedOpen);
            }
          }}
        >
          <span
            className='mr-2 font-weight-bold text-dark'
            style={{ fontSize: '1.25rem', lineHeight: 1, userSelect: 'none' }}
            aria-hidden
          >
            {scannedOpen ? '−' : '+'}
          </span>
          <span className='font-weight-bold mb-0'>Проскановані товари</span>
          <span className='text-muted small ml-2'>({scannedItems.length})</span>
        </div>
        {scannedOpen ? (
          <div className='card-body py-3'>
          <label className='font-weight-bold mb-1 d-block'>Сканування штрихкоду</label>
          <div className='position-relative'>
            <input
              ref={inputRef}
              type='text'
              className='form-control'
              style={{ height: '56px', fontSize: '1.15rem', paddingRight: '2.4rem' }}
              placeholder='Скануйте або введіть штрихкод'
              value={barcode}
              onChange={(e) => {
                const value = e.target.value || '';
                if (value.includes('\n') || value.includes('\r')) {
                  const normalized = value.replace(/\r/g, '\n');
                  const firstCode = normalized.split('\n').find((part) => part.trim() !== '');
                  if (firstCode) submitScan(firstCode.trim());
                  setBarcode('');
                  return;
                }
                setBarcode(value);
              }}
              onKeyDown={(e) => e.key === 'Enter' && submitScan()}
              disabled={loading}
            />
            <span className='position-absolute' style={{ right: '0.7rem', top: '50%', transform: 'translateY(-50%)' }}>
              {submitting ? (
                <IncomingScanStatusBadge loading />
              ) : (
                <i className='bi bi-upc-scan text-muted' style={{ fontSize: '1.25rem' }} />
              )}
            </span>
          </div>
          {error ? <div className='alert alert-danger mt-2 mb-0 py-2 small'>{error}</div> : null}

          {loading && !shipment ? (
            <div className='mt-2'>Завантаження...</div>
          ) : (
            <div className='mt-2'>
              {groupedItems.map((group) => (
                <div key={group.label}>
                  <div className='small text-muted font-weight-bold mb-2 mt-2'>{group.label}</div>
                  {group.items.map((it) => (
                    <IncomingShipmentScanCard
                      key={`${it.barcode}-${it.id}`}
                      item={it}
                      bindItemId={bindItemId}
                      bindSearch={bindSearch}
                      setBindSearch={setBindSearch}
                      bindSearchResults={bindSearchResults}
                      bindSearchLoading={bindSearchLoading}
                      bindSubmitting={bindSubmitting}
                      openBindDropdown={openBindDropdown}
                      bindProductToItem={bindProductToItem}
                      itemPhotoUploadingId={itemPhotoUploadingId}
                      uploadItemPhoto={uploadItemPhoto}
                      deleteItemPhoto={deleteItemPhoto}
                      deleteScannedItem={deleteScannedItem}
                      saveItemQuantity={saveItemQuantity}
                      itemDeletingId={itemDeletingId}
                      itemQuantitySavingId={itemQuantitySavingId}
                    />
                  ))}
                </div>
              ))}
              {!scannedItems.length ? <div className='text-muted'>Просканованих позицій ще немає</div> : null}
            </div>
          )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default IncomingShipmentDetailMobileView;
